import cx from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";

import Collapsible from "@/mk2/apps/bazaar/components/Collapsible";
import ProductPhoto from "@/mk2/apps/bazaar/components/ProductPhoto";
import { Btn, BtnType } from "@/mk2/components/Btn";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { PhotoEntity } from "@/mk2/schemas";
import { BZ2_DETAIL_HIDE_IMAGES, BZ2_DETAIL_SHOW_ALL_IMAGES } from "@/mk/autogenerated/translations/SecondaryImages.f953506405058906cb155e5f635e410f";

import styles from "./SecondaryImages.mscss";

interface OwnProps {
    className?: string;
    collapsed?: boolean;
    collapsible: boolean;
    images: PhotoEntity[];
    alt: string;

    onToggle(event: React.MouseEvent<HTMLAnchorElement>);
}

type Props = OwnProps;

const SecondaryImages: React.FunctionComponent<Props> = ({
    className,
    collapsed = false,
    collapsible = false,
    images,
    alt,
    onToggle,
}) => {
    const [loadedImages, setLoadedImages] = useState<number>(0);
    const [imagesComplete, setImagesComplete] = useState<boolean>(false);
    const imageRefs = useRef(new Map<number, HTMLImageElement>());

    const onLoad = useEventCallback(() => {
        setLoadedImages((s) => s + 1);
    }, [setLoadedImages]);

    const onImgRef = useCallback(
        (imageId: number, ref: HTMLImageElement) => {
            imageRefs.current.set(imageId, ref);
        },
        [imageRefs],
    );

    useEffect(() => {
        // Reset loaded counter if images has changed
        setLoadedImages(0);
        // Check if every image is loaded when images prop has changed
        setImagesComplete(images.every((image) => imageRefs.current.get(image.id).complete));
    }, [setLoadedImages, setImagesComplete, images, imageRefs]);

    // We are using s460xn
    const potentialMaxHeight = images.reduce((acc, cur) => acc + cur.height / (cur.width / 460), 0);

    return (
        <div className={cx(styles.SecondaryImages, className)}>
            <Collapsible
                collapsed={collapsible && collapsed}
                height={120}
                maxHeight={
                    loadedImages === images.length || imagesComplete ? null : potentialMaxHeight
                }
            >
                {images.map((image) => (
                    <SecondaryImage
                        image={image}
                        alt={alt}
                        key={image.id}
                        onLoad={onLoad}
                        imgRef={onImgRef}
                    />
                ))}
                {collapsible && collapsed && <div className={styles.SecondaryImages__mask} />}
            </Collapsible>
            {collapsible && (
                <div
                    className={cx(
                        styles.SecondaryImages__button,
                        styles[`SecondaryImages__button--${collapsed ? "" : "un"}collapsed`],
                    )}
                >
                    <Btn
                        type={BtnType.Blue}
                        onClick={onToggle}
                        label={
                            <>
                                {collapsed ? BZ2_DETAIL_SHOW_ALL_IMAGES : BZ2_DETAIL_HIDE_IMAGES}
                                &nbsp;(
                                {images.length})
                            </>
                        }
                    />
                </div>
            )}
        </div>
    );
};

SecondaryImages.displayName = "SecondaryImages";

export default React.memo<Props>(SecondaryImages);

interface SecondaryImageOwnProps {
    image: PhotoEntity;
    alt: string;

    imgRef(imageId: number, ref: HTMLImageElement);
    onLoad(imageId: number);
}

let SecondaryImage: React.FunctionComponent<SecondaryImageOwnProps> = ({
    image,
    alt,
    imgRef,
    onLoad,
}) => {
    const onLoadCallback = useEventCallback(() => {
        onLoad(image.id);
    }, [onLoad, image.id]);

    const imgRefCallback = useCallback(
        (ref: HTMLImageElement) => {
            imgRef(image.id, ref);
        },
        [imgRef, image.id],
    );

    return (
        <ProductPhoto
            className={styles.SecondaryImages__photo}
            photo={image}
            alt={alt}
            onLoad={onLoadCallback}
            imgRef={imgRefCallback}
        />
    );
};

SecondaryImage.displayName = "SecondaryImage";

SecondaryImage = React.memo<SecondaryImageOwnProps>(SecondaryImage);
